import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Camera, Hash, ToggleLeft, FileText, Upload, X, Activity, MonitorSmartphone, Ruler, Gauge } from 'lucide-react';
import { useAddEvidence } from '@/hooks/useOptiBayEvidence';
import { uploadArtifact } from '@/lib/artifacts/artifactUpload';
import { ARTIFACT_SOURCES, LINK_ENTITY_TYPES, LINK_ROLES } from '@/lib/artifacts/constants';
import type { OptiBayEvidenceType } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayEvidenceCaptureProps {
  sessionId: string;
  sessionTestPathId: string;
  shopId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const TYPE_OPTIONS: { type: OptiBayEvidenceType; label: string; icon: typeof Camera }[] = [
  { type: 'photo', label: 'Photo', icon: Camera },
  { type: 'waveform', label: 'Waveform', icon: Activity },
  { type: 'scan_data', label: 'Scan Data', icon: MonitorSmartphone },
  { type: 'numeric', label: 'Measurement', icon: Hash },
  { type: 'meter_reading', label: 'Meter', icon: Gauge },
  { type: 'alignment', label: 'Alignment', icon: Ruler },
  { type: 'boolean', label: 'Yes/No', icon: ToggleLeft },
  { type: 'note', label: 'Note', icon: FileText },
];

export function OptiBayEvidenceCapture({
  sessionId,
  sessionTestPathId,
  shopId,
  onClose,
  onSuccess,
}: OptiBayEvidenceCaptureProps) {
  const addEvidence = useAddEvidence();
  const [activeType, setActiveType] = useState<OptiBayEvidenceType>('photo');
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [valueNumeric, setValueNumeric] = useState('');
  const [unit, setUnit] = useState('');
  const [valueBoolean, setValueBoolean] = useState(false);
  const [valueText, setValueText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Alignment-specific
  const [alignBefore, setAlignBefore] = useState('');
  const [alignAfter, setAlignAfter] = useState('');

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let artifactId: string | undefined;

      // Upload file for photo, waveform, scan_data
      if ((activeType === 'photo' || activeType === 'waveform' || activeType === 'scan_data') && file) {
        const result = await uploadArtifact({
          shopId,
          file,
          source: ARTIFACT_SOURCES.PWA_UPLOAD,
          title: label || file.name,
          link: {
            entityType: LINK_ENTITY_TYPES.OPTIBAY_SESSION_EVIDENCE,
            entityId: sessionTestPathId,
            role: LINK_ROLES.DIAGNOSTIC_EVIDENCE,
          },
        });
        artifactId = result.id;
      }

      // Build value_text for alignment (store before/after as JSON)
      let finalValueText = activeType === 'note' || activeType === 'scan_data' ? valueText : undefined;
      let finalValueNumeric: number | undefined;
      const finalUnit = unit || undefined;

      if (activeType === 'numeric' || activeType === 'meter_reading') {
        finalValueNumeric = valueNumeric ? Number(valueNumeric) : undefined;
      }

      if (activeType === 'alignment') {
        const before = alignBefore ? Number(alignBefore) : null;
        const after = alignAfter ? Number(alignAfter) : null;
        finalValueNumeric = after ?? undefined;
        finalValueText = JSON.stringify({ before, after });
      }

      await addEvidence.mutateAsync({
        sessionId,
        sessionTestPathId,
        evidenceType: activeType,
        artifactId,
        label: label || undefined,
        valueNumeric: finalValueNumeric,
        valueBoolean: activeType === 'boolean' ? valueBoolean : undefined,
        valueText: finalValueText,
        unit: (activeType === 'numeric' || activeType === 'meter_reading' || activeType === 'alignment') ? finalUnit : undefined,
      });

      onSuccess();
      onClose();
    } catch (e) {
      console.error('Evidence capture failed:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = (() => {
    switch (activeType) {
      case 'photo':
      case 'waveform':
        return !!file;
      case 'scan_data':
        return !!file || !!valueText.trim();
      case 'numeric':
      case 'meter_reading':
        return !!valueNumeric;
      case 'alignment':
        return !!alignBefore || !!alignAfter;
      case 'boolean':
        return true;
      case 'note':
        return !!valueText.trim();
      default:
        return false;
    }
  })();

  const isFileType = activeType === 'photo' || activeType === 'waveform' || activeType === 'scan_data';

  return (
    <div className="border rounded-lg p-4 space-y-4 bg-card">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold">Add Evidence</h5>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Type selector */}
      <div className="flex gap-1 flex-wrap">
        {TYPE_OPTIONS.map(({ type, label: lbl, icon: Icon }) => (
          <button
            key={type}
            type="button"
            onClick={() => setActiveType(type)}
            className={cn(
              'flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors',
              activeType === type
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            )}
          >
            <Icon className="h-3 w-3" />
            {lbl}
          </button>
        ))}
      </div>

      {/* File upload — shared by photo, waveform, scan_data */}
      {isFileType && (
        <div className="space-y-2">
          <Label className="text-xs">
            {activeType === 'photo' ? 'Photo / Image' : activeType === 'waveform' ? 'Waveform Capture' : 'Scan Data Export'}
          </Label>
          {file ? (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">{file.name}</Badge>
              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setFile(null)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:border-primary/50 transition-colors">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {activeType === 'waveform' ? 'Upload scope capture (image/PDF)' :
                 activeType === 'scan_data' ? 'Upload scan screenshot or export' :
                 'Click to select file'}
              </span>
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)}
              />
            </label>
          )}
          <Input
            placeholder="Label (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          {/* Scan data: optional DTC text field */}
          {activeType === 'scan_data' && (
            <Textarea
              placeholder="DTC codes or scan notes (optional)…"
              value={valueText}
              onChange={e => setValueText(e.target.value)}
              className="min-h-[50px] text-xs"
            />
          )}
        </div>
      )}

      {/* Numeric measurement */}
      {activeType === 'numeric' && (
        <div className="space-y-2">
          <Input
            placeholder="Label (e.g. Peak voltage)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Value"
              value={valueNumeric}
              onChange={e => setValueNumeric(e.target.value)}
              className="h-8 text-xs flex-1"
            />
            <Input
              placeholder="Unit (PSI, V, °F)"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              className="h-8 text-xs w-24"
            />
          </div>
        </div>
      )}

      {/* Meter reading — same as numeric layout */}
      {activeType === 'meter_reading' && (
        <div className="space-y-2">
          <Input
            placeholder="Label (e.g. Battery voltage)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Reading"
              value={valueNumeric}
              onChange={e => setValueNumeric(e.target.value)}
              className="h-8 text-xs flex-1"
            />
            <Input
              placeholder="Unit (V, A, Ω)"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              className="h-8 text-xs w-24"
            />
          </div>
        </div>
      )}

      {/* Alignment — before/after numeric fields */}
      {activeType === 'alignment' && (
        <div className="space-y-2">
          <Input
            placeholder="Label (e.g. Front toe, Camber LF)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="flex gap-2">
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] text-muted-foreground">Before</Label>
              <Input
                type="number"
                placeholder="Before"
                value={alignBefore}
                onChange={e => setAlignBefore(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex-1 space-y-1">
              <Label className="text-[10px] text-muted-foreground">After</Label>
              <Input
                type="number"
                placeholder="After"
                value={alignAfter}
                onChange={e => setAlignAfter(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="w-20 space-y-1">
              <Label className="text-[10px] text-muted-foreground">Unit</Label>
              <Input
                placeholder="°, mm"
                value={unit}
                onChange={e => setUnit(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>
        </div>
      )}

      {/* Boolean */}
      {activeType === 'boolean' && (
        <div className="space-y-2">
          <Input
            placeholder="Label (e.g. Power at connector?)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          <div className="flex items-center gap-3">
            <Switch checked={valueBoolean} onCheckedChange={setValueBoolean} />
            <span className="text-sm font-medium">{valueBoolean ? 'Yes' : 'No'}</span>
          </div>
        </div>
      )}

      {/* Note */}
      {activeType === 'note' && (
        <div className="space-y-2">
          <Input
            placeholder="Label (optional)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            className="h-8 text-xs"
          />
          <Textarea
            placeholder="Technician observation…"
            value={valueText}
            onChange={e => setValueText(e.target.value)}
            className="min-h-[60px] text-xs"
          />
        </div>
      )}

      <Button
        size="sm"
        className="w-full"
        disabled={!canSubmit || isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? 'Saving…' : 'Save Evidence'}
      </Button>
    </div>
  );
}
